import React, { useLayoutEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import './styles.css';
import Panel from '../Panel';
import { Tree, type NodeRendererProps } from 'react-arborist';
import IconButton from '../IconButton';
import { Plus as PlusIcon, Minus as MinusIcon, DoubleArrowLeft, DoubleArrowRight } from '../Icons';
import { orderBy } from 'lodash';
import { BUILTIN_DATA_TYPES } from '../../stores/model/metamodel/data_type';
import type { IModel } from '../../stores/model';
import type { IMetaModel } from '../../stores/model/metamodel';
import type { IMethod } from '../../stores/model/metamodel/method';
import type { IFSM } from '../../stores/model/metamodel/fsm';

/**
 * Props for {@link ModelExplorer}.
 *
 * - model: the MERODE model (may be null while the version content loads; the
 *   explorer is only rendered with loaded content, so it is dereferenced below).
 * - visible: is the ModelExplorer visible? The Panel that wraps the model
 *   explorer content is always rendered, but in collapsed mode when this prop
 *   is false.
 * - requestClose: callback to close the model explorer
 * - requestOpen: callback to open the model explorer
 * - selected: id of the currently selected element
 * - onSelect: callback to call when an element is selected. Arguments: id,
 *   inspectable.
 * - onShowFSM: callback to show the FSM for the given element id.
 */
export interface ModelExplorerProps {
  /** The model version id (passed through by the parent, not read here). */
  id?: string;
  model: IModel | null;
  visible: boolean;
  requestClose: () => void;
  requestOpen: () => void;
  selected: string | null;
  onSelect?: (id: string, inspectable: boolean) => void;
  onShowFSM?: (id: string) => void;
}

/**
 * An element passed to {@link getTreeObject}. The common fields drive the node
 * (`id`/`name`/`active`/`onClick`); `methods`/`fsms` are carrier fields read by
 * the object-tree subtree generators; `inspectable`/`showFSM` are forwarded to
 * the select handler.
 */
interface TreeElement {
  id: string;
  name: React.ReactNode;
  selectable?: boolean;
  inspectable: boolean;
  showFSM?: boolean;
  methods?: IMethod[];
  fsms?: IFSM[];
}

interface ExplorerTreeNode {
  id: string;
  name: React.ReactNode;
  selectable: boolean;
  inspectable: boolean;
  showFSM?: boolean;
  children?: ExplorerTreeNode[];
}

/**
 * The root descriptor for a (sub)tree. Every call site (and every subtree
 * generator) supplies an explicit `{ id, name }` object, so the legacy
 * `root.id || root` string fallback was dead — the root is always this object.
 */
interface TreeRoot {
  id: string;
  name: React.ReactNode;
}

/** A subtree configuration: generators for the recursive `getTreeObject` call. */
interface SubtreeConfig {
  root: (element: TreeElement) => TreeRoot;
  elements: (element: TreeElement) => TreeElement[];
}

function useMeasuredHeight<T extends HTMLElement>(enabled: boolean) {
  const ref = useRef<T | null>(null);
  const [height, setHeight] = useState(1);

  useLayoutEffect(() => {
    if (!enabled) return;

    const element = ref.current;
    if (element == null) return;

    const update = () => {
      setHeight(Math.max(1, element.clientHeight));
    };

    update();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled]);

  return [ref, height] as const;
}

/**
 * Render the model explorer.
 *
 * Uses react-arborist for the treeview.
 */
const ModelExplorer = observer(function ModelExplorer(props: ModelExplorerProps) {
  const { model, visible, requestClose, requestOpen, selected, onSelect, onShowFSM } = props;
  const [treeWrapperRef, treeHeight] = useMeasuredHeight<HTMLDivElement>(visible);

  // The metamodel reads as the hand-declared `IMetaModel` interface so its
  // array views (`objectTypes`, ...) resolve to concrete element types rather
  // than the loose MST instance shape. The instance node IS the `IMetaModel`;
  // only the MPC `type` enum widens (see the rsForm/forms boundary casts), so
  // cast at this single read boundary. `model` is non-null here (the explorer is
  // only rendered with loaded version content).
  const metamodel = model!.metamodel as unknown as IMetaModel;

  // sort object types by name
  const sortedObjectTypes = orderBy(metamodel.objectTypes, ['name']);

  // sort event types by name
  const sortedEvents = orderBy(metamodel.eventTypes, ['name']);

  // sort dependencies by displayName
  const sortedDependencies = orderBy(metamodel.dependencies, ['displayName']);

  // sort inheritances by displayString
  const sortedInheritances = orderBy(metamodel.inheritances, ['displayString']);

  // sort methods: SPECIALISED / INHERITED / OWNED / ACQUIRED, then by name
  const sortedMethods = (object: TreeElement) =>
    orderBy(object.methods, ['provenancePriority', 'name'], ['asc', 'asc']);

  // Sort the FSMs for the given object by name
  const sortedFSMs = (object: TreeElement) => orderBy(object.fsms, ['name']);

  // Sort data types: first builtin, then custom ordered by name
  const sortedDataTypes = BUILTIN_DATA_TYPES.map(t => ({
    id: `datatype-${t}`,
    name: t,
  })).concat(orderBy(metamodel.dataTypes, ['name']));

  const onShowFSMHandler = (id: string) => {
    if (onShowFSM != null) {
      onShowFSM(id);
    }
  };

  // On click => select clicked element
  const onClick = (
    e: React.MouseEvent,
    id: string,
    inspectable: boolean,
    showFSM?: boolean
  ) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(id, inspectable);
    }
    if (showFSM) {
      onShowFSMHandler(id);
    }
  };

  /**
   * Utility function to generate a tree node for react-arborist
   *
   * @param {Object} root The root for the tree
   * - {String} id: the id for the root node (for keeping toggle state)
   * - {String} name: the name to show on the root of the tree
   * @param {Array[Object]} elements The elements in the tree. Each element
   * needs:
   * - {String} id: the id for the element (for keeping toggle state and for the
   *   onSelect callback)
   * - {String} name: the name to show
   * - {Boolean} selectable: is selection enabled for the element
   * - {Boolean} inspectable: should the inspector open if the element is selected
   * - ...: any information needed for the subtrees: the element will be passed
   *   to configuration generators for the subtrees (see below)
   * @param {Array[Object]} subtrees Configuration for subtrees. Used in a
   * recursive call to this function. Each subtree needs:
   * - {Function} root: function to generate the root argument for the recursive
   *   call. Arguments: element.
   * - {Function} elements: function to generate the elements argument for the
   *   recursive call. Arguments: element.
   */
  const getTreeObject = (
    root: TreeRoot,
    elements: TreeElement[],
    subtrees: SubtreeConfig[] = []
  ): ExplorerTreeNode => {
    const children: ExplorerTreeNode[] = elements.map(element => {
      const result: ExplorerTreeNode = {
        name: element.name,
        id: element.id,
        selectable: element.selectable === true,
        inspectable: element.inspectable,
        showFSM: element.showFSM,
      };
      if (subtrees.length > 0) {
        result.children = subtrees.map(st =>
          getTreeObject(st.root(element), st.elements(element))
        );
      }
      return result;
    });
    const id = root.id;
    const name = root.name;
    return {
      id,
      name,
      selectable: false,
      inspectable: false,
      children: children,
    };
  };

  const treeData: ExplorerTreeNode[] = [
    getTreeObject(
      { name: 'Objects', id: 'objects' },
      sortedObjectTypes.map(o => ({
        id: o.id,
        name: o.name,
        selectable: true,
        inspectable: true,
        methods: o.methods,
        fsms: o.fsms,
      })),
      [
        {
          root: object => ({
            name: 'Methods',
            id: `methods-${object.id}`,
          }),
          elements: object =>
            sortedMethods(object).map(m => ({
              id: m.id,
              name: m.displayStringWithMetadata,
              selectable: true,
              inspectable: true,
            })),
        },
        {
          root: object => ({
            name: 'FSMs',
            id: `fsms-${object.id}`,
          }),
          elements: object =>
            sortedFSMs(object).map(f => ({
              id: f.id,
              name: f.displayName,
              selectable: true,
              inspectable: true,
              showFSM: true,
            })),
        },
      ]
    ),
    getTreeObject(
      { name: 'Events', id: 'events' },
      sortedEvents.map(e => ({
        id: e.id,
        name: e.name,
        selectable: true,
        inspectable: true,
      }))
    ),
    getTreeObject(
      { name: 'Dependencies', id: 'dependencies' },
      sortedDependencies.map(d => ({
        id: d.id,
        name: d.displayName,
        selectable: true,
        inspectable: true,
      }))
    ),
    getTreeObject(
      { name: 'Inheritance', id: 'inheritances' },
      sortedInheritances.map(i => ({
        id: i.id,
        name: i.utf8DisplayString,
        selectable: true,
        inspectable: true,
      }))
    ),
    getTreeObject(
      { name: 'Data Types', id: 'data_types' },
      sortedDataTypes.map(d => ({
        id: d.id,
        name: d.name,
        selectable: false,
        inspectable: false,
      }))
    ),
  ];

  const renderNode = ({ node, style }: NodeRendererProps<ExplorerTreeNode>) => {
    const data = node.data;
    const hasChildren = node.isInternal;
    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      node.toggle();
    };
    const handleNodeClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (data.selectable) {
        onClick(e, data.id, data.inspectable, data.showFSM);
      } else if (hasChildren) {
        node.toggle();
      }
    };

    return (
      <div style={style} className="node-row text-grey2">
        <div
          onClick={handleNodeClick}
          className="node-container relative flex h-6 min-w-0 ml-1 items-start"
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={handleToggle}
              className="node-toggle flex-[0_0_24px] h-6 flex items-center justify-center p-0 border-0 bg-transparent text-inherit cursor-pointer select-none"
            >
              {node.isOpen ? <MinusIcon /> : <PlusIcon />}
            </button>
          ) : (
            <span className="node-toggle-placeholder flex-[0_0_24px]" />
          )}
          <div
            className={classNames('node-header inline-block align-top', {
              'has-children text-black': hasChildren,
              'text-grey2': !hasChildren,
            })}
          >
            <div
              className={classNames(
                'node overflow-hidden leading-6 text-ellipsis whitespace-nowrap cursor-pointer select-none',
                {
                  'hover:text-primary': !hasChildren,
                  'active font-medium underline': node.isSelected,
                }
              )}
            >
              {data.name}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the header in visible mode
  const renderHeader = () => {
    return (
      <div className="model-explorer-view-header h-full flex flex-row justify-center items-center pl-[15px]">
        <div className="title flex-1">Model Explorer</div>
        <div className="close-wrapper flex-[0_0_37px]">
          <IconButton onClick={requestClose}>
            <DoubleArrowLeft />
          </IconButton>
        </div>
      </div>
    );
  };

  // Render the header in collapsed mode
  const renderCollapsedHeader = () => {
    return (
      <div className="model-explorer-collapsed-header">
        <IconButton onClick={requestOpen} variant="narrow">
          <DoubleArrowRight />
        </IconButton>
      </div>
    );
  };

  // Render content (only when in visible mode)
  const renderContent = () => {
    if (visible) {
      return (
        <div ref={treeWrapperRef} className="model-explorer-tree-wrapper h-full min-h-px">
          <Tree
            data={treeData}
            width="100%"
            height={treeHeight}
            rowHeight={24}
            indent={20}
            overscanCount={4}
            openByDefault={false}
            initialOpenState={{ objects: true, events: true, dependencies: true }}
            selection={selected ?? undefined}
            disableSelect={(node) => !node.selectable}
            disableEdit
            disableDrag
            disableDrop
            disableMultiSelection
            className="model-explorer-tree focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            {renderNode}
          </Tree>
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <Panel
      className="model-explorer-view w-[300px] shrink-0 z-[9] mr-[15px]"
      type="side"
      header={renderHeader}
      collapsed={!visible}
      collapsedHeader={renderCollapsedHeader}
    >
      {renderContent()}
    </Panel>
  );
});

export default ModelExplorer;
